import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, Link } from 'react-router-dom';
import {
    AppBar,
    Box,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    Avatar,
    Menu,
    MenuItem,
    Badge,
    Tooltip,
    Chip,
    Fade,
    Paper,
    Button,
    Stack,
    useTheme,
    useMediaQuery,
    Alert,
    BottomNavigation,
    BottomNavigationAction,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Fab
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    Receipt as ReceiptIcon,
    Payment as PaymentIcon,
    SupportAgent as SupportIcon,
    AccountCircle as AccountIcon,
    ExitToApp as LogoutIcon,
    Notifications as NotificationsIcon,
    MoreVert as MoreIcon,
    Settings as SettingsIcon,
    Speed as SpeedIcon,
    Wifi as WifiIcon,
    ArrowForwardIos as ArrowIcon,
    Home as HomeIcon,
    Info as InfoIcon,
    MarkEmailRead as MarkReadIcon,
    Warning as WarningIcon,
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    Edit as EditIcon,
    LiveHelp as HelpIcon,
    CardGiftcard as GiftIcon,
    Brightness4 as DarkModeIcon,
    Brightness7 as LightModeIcon
} from '@mui/icons-material';
import { useClientAuth } from '../contexts/ClientAuthContext';
import { useClientTheme } from '../contexts/ClientThemeContext';
import InactiveServiceWarning from '../components/InactiveServiceWarning';
import NotificationCenter from '../components/notifications/NotificationCenter';
import HelpTutorialDialog from '../components/HelpTutorialDialog';
import TermsModal from '../components/legal/TermsModal';
import ComplaintsBookModal from '../components/legal/ComplaintsBookModal';

const drawerWidth = 250;

const menuItems = [
    { name: 'Inicio', icon: <HomeIcon />, path: '/client/dashboard' },
    { name: 'Recibos', icon: <PaymentIcon />, path: '/client/payments' },
    { name: 'Tráfico', icon: <SpeedIcon />, path: '/client/traffic' },
    { name: 'Promociones', icon: <GiftIcon />, path: '/client/promotions' },
    { name: 'Soporte', icon: <SupportIcon />, path: '/client/support' },
    { name: 'Mi Perfil', icon: <AccountIcon />, path: '/client/profile' }
];

const ClientLayout = () => {
    const { client, clientToken, logoutClient, isActive, services, switchActiveService, updateServiceAlias } = useClientAuth();
    const { mode, toggleTheme } = useClientTheme();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
    const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);
    const [serviceMenuAnchor, setServiceMenuAnchor] = useState(null);
    const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
    const openMoreMenu = Boolean(moreMenuAnchor);
    const openServiceMenu = Boolean(serviceMenuAnchor);
    const navigate = useNavigate();
    const [companySettings, setCompanySettings] = useState({ company_name: 'YinoWISP', company_logo: '' });

    // Estados para el diálogo de edición de alias
    const [editAliasOpen, setEditAliasOpen] = useState(false);
    const [selectedServiceForAlias, setSelectedServiceForAlias] = useState(null);
    const [newAliasValue, setNewAliasValue] = useState('');
    const [tutorialOpen, setTutorialOpen] = useState(false);
    const [termsOpen, setTermsOpen] = useState(false);
    const [complaintsOpen, setComplaintsOpen] = useState(false);
    const [savingAlias, setSavingAlias] = useState(false);

    const handleOpenEditAliasDialog = (service) => {
        setSelectedServiceForAlias(service);
        setNewAliasValue(service.alias || '');
        setEditAliasOpen(true);
    };

    const handleCloseEditAliasDialog = () => {
        setEditAliasOpen(false);
        setSelectedServiceForAlias(null);
        setNewAliasValue('');
    };

    const handleMoreMenuClose = () => {
        setMoreMenuAnchor(null);
    };

    const handleSaveAlias = async () => {
        if (!selectedServiceForAlias) return;
        setSavingAlias(true);
        try {
            const res = await updateServiceAlias(selectedServiceForAlias.id, newAliasValue.trim());
            if (res.success) {
                handleCloseEditAliasDialog();
            } else {
                alert(res.error || 'Error al guardar el nombre personalizado');
            }
        } catch (err) {
            console.error('Error saving service alias:', err);
            alert('Ocurrió un error inesperado');
        } finally {
            setSavingAlias(false);
        }
    };

    const handleServiceMenuClick = (event) => {
        setServiceMenuAnchor(event.currentTarget);
    };

    const handleServiceMenuClose = () => {
        setServiceMenuAnchor(null);
    };

    const handleServiceSelect = async (srvId) => {
        handleServiceMenuClose();
        if (srvId !== client?.id) {
            await switchActiveService(srvId);
        }
    };

    const clientServiceLabel = client ? (services.find(s => s.id === client.id)?.alias || services.find(s => s.id === client.id)?.address || client.plan || 'Servicio') : 'Cargando...';
    const firstName = client?.fullName ? client.fullName.split(' ')[0] : 'Cliente';

    useEffect(() => {
        const fetchCompanySettings = async () => {
            try {
                const response = await fetch('/api/public/company-settings');
                if (response.ok) {
                    const data = await response.json();
                    setCompanySettings({
                        company_name: data.company_name || 'YinoWISP',
                        company_logo: data.company_logo || ''
                    });
                }
            } catch (error) {
                console.error('Error fetching company settings for client layout:', error);
            }
        };
        fetchCompanySettings();
    }, []);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Estado para el modal de servicio inactivo
    const [showInactiveWarning, setShowInactiveWarning] = useState(false);

    // Estado para mostrar la alerta de servicio inactivo en la parte superior
    const [showInactiveAlert, setShowInactiveAlert] = useState(!isActive);

    // Estado para controlar cuándo mostrar el componente de carga
    const [isLoading, setIsLoading] = useState(true);

    // Mostrar el modal de advertencia cuando se detecta un servicio inactivo
    useEffect(() => {
        if (client && client.status !== 'active') {
            setShowInactiveWarning(true);
            setShowInactiveAlert(true);
        } else {
            setShowInactiveWarning(false);
            setShowInactiveAlert(false);
        }
    }, [client]);

    useEffect(() => {
        // Iniciar el proceso de carga
        setIsLoading(true);

        if (client) {
            setIsLoading(false);
        } else {
            setIsLoading(false);
        }
    }, [client]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleProfileMenuOpen = (event) => {
        setProfileMenuAnchor(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setProfileMenuAnchor(null);
    };

    const handleMobileMenuOpen = (event) => {
        setMobileMoreAnchorEl(event.currentTarget);
    };

    const handleMobileMenuClose = () => {
        setMobileMoreAnchorEl(null);
    };

    const handleMenuItemClick = (path) => {
        navigate(path);
        setMobileOpen(false);
    };

    const handleCloseInactiveWarning = () => {
        setShowInactiveWarning(false);
    };

    const handleDismissAlert = () => {
        setShowInactiveAlert(false);
    };

    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
    const isProfileMenuOpen = Boolean(profileMenuAnchor);

    // Items del menú móvil
    const mobileMenuItems = [
        <MenuItem key="prof-mobile" onClick={handleProfileMenuOpen}>
            <IconButton
                size="large"
                aria-haspopup="true"
                color="inherit"
            >
                <Avatar sx={{ width: 32, height: 32 }}>
                    {client?.fullName?.charAt(0) || 'C'}
                </Avatar>
            </IconButton>
            <p>Perfil</p>
        </MenuItem>
    ];

    // Items del menú de perfil
    const profileMenuItems = [
        <Box key="profile-header" sx={{ px: 2, py: 1, textAlign: 'center' }}>
            <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 1, bgcolor: 'primary.main' }}>
                {client?.fullName?.charAt(0) || 'C'}
            </Avatar>
            <Typography variant="subtitle1" fontWeight="bold">
                {client?.fullName || 'Cliente'}
            </Typography>
            <Chip
                size="small"
                label={client?.plan || 'Plan Básico'}
                color={isActive ? "primary" : "error"}
                variant="outlined"
                sx={{ mt: 0.5 }}
            />
            {!isActive && (
                <Typography variant="caption" color="error" display="block" mt={0.5}>
                    Servicio suspendido
                </Typography>
            )}
        </Box>,
        <Divider key="profile-divider-1" sx={{ my: 1 }} />,
        <MenuItem key="profile-menu-item" onClick={() => {
            handleProfileMenuClose();
            navigate('/client/profile');
        }}>
            <ListItemIcon>
                <AccountIcon fontSize="small" />
            </ListItemIcon>
            Mi Perfil
        </MenuItem>,
        <MenuItem key="settings-menu-item" onClick={() => {
            handleProfileMenuClose();
            navigate('/client/profile/settings');
        }}>
            <ListItemIcon>
                <SettingsIcon fontSize="small" />
            </ListItemIcon>
            Configuración
        </MenuItem>,
        <Divider key="profile-divider-2" />,
        <MenuItem key="logout-menu-item" onClick={() => {
            handleProfileMenuClose();
            logoutClient();
        }}>
            <ListItemIcon>
                <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Cerrar Sesión
        </MenuItem>
    ];

    // Contenido del drawer
    const drawer = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', color: '#ffffff' }}>
            <Toolbar sx={{
                height: 80,
                minHeight: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                py: 0,
                px: 3
            }}>
                <Typography variant="h5" noWrap component="div" sx={{ color: '#00e5ff', fontWeight: 900, fontFamily: '"Outfit", "Inter", sans-serif', letterSpacing: 0.5 }}>
                    YinoWISP
                </Typography>
            </Toolbar>
            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />
            <Box sx={{ p: 2 }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.12)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        color: '#ffffff',
                        borderRadius: 3,
                        mb: 1
                    }}
                >
                    <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem', fontWeight: 'medium' }}>
                        {client?.plan || 'Plan de Internet'}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SpeedIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.9)' }} />
                        {client?.speeds?.download || 10} / {client?.speeds?.upload || 10} Mbps
                    </Typography>
                    {!isActive && (
                        <Chip
                            label="Suspendido"
                            size="small"
                            color="error"
                            variant="filled"
                            sx={{ mt: 1, color: 'white', fontWeight: 'bold', height: 20, fontSize: '0.65rem' }}
                        />
                    )}
                </Paper>
            </Box>
            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />
            <List sx={{ flex: 1, px: 1.5 }}>
                {menuItems.map((item) => (
                    <ListItem key={item.name} disablePadding sx={{ mb: 0.75 }}>
                        <ListItemButton
                            onClick={() => handleMenuItemClick(item.path)}
                            selected={window.location.pathname === item.path}
                            sx={{
                                borderRadius: 2.5,
                                py: 1.2,
                                color: 'rgba(255, 255, 255, 0.8)',
                                '& .MuiListItemIcon-root': {
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    minWidth: 40
                                },
                                '&.Mui-selected': {
                                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                                    color: '#ffffff',
                                    fontWeight: 'bold',
                                    '& .MuiListItemIcon-root': {
                                        color: '#ffffff',
                                    },
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.25)',
                                    },
                                },
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                                    color: '#ffffff',
                                    '& .MuiListItemIcon-root': {
                                        color: '#ffffff',
                                    }
                                },
                            }}
                        >
                            <ListItemIcon>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText 
                                primary={item.name} 
                                primaryTypographyProps={{ 
                                    fontWeight: window.location.pathname === item.path ? 'bold' : 'medium',
                                    fontSize: '0.9rem'
                                }} 
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
            <Box sx={{ p: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.75rem', fontWeight: 500 }}>
                    © {new Date().getFullYear()} {companySettings.company_name}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography
                        variant="caption"
                        onClick={() => setTermsOpen(true)}
                        sx={{
                            color: '#ffffff',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            textUnderlineOffset: '3px',
                            '&:hover': { color: '#bfdbfe' }
                        }}
                    >
                        Términos
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.75rem' }}>|</Typography>
                    <Box
                        onClick={() => setComplaintsOpen(true)}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            bgcolor: 'rgba(255, 255, 255, 0.15)',
                            color: '#fef08a',
                            border: '1px solid rgba(254, 240, 138, 0.4)',
                            px: 1.2,
                            py: 0.4,
                            borderRadius: '12px',
                            cursor: 'pointer',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            transition: 'all 0.2s',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.25)',
                                color: '#ffffff',
                                borderColor: '#ffffff'
                            }
                        }}
                    >
                        📘 Libro Reclamaciones
                    </Box>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />

            {/* Modal de advertencia de servicio inactivo */}
            <InactiveServiceWarning
                open={showInactiveWarning}
                onClose={handleCloseInactiveWarning}
                autoCloseTime={10000}
            />

            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                    background: theme.palette.mode === 'dark' ? '#0f172a' : '#f5f7fb',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    boxShadow: 'none',
                    color: theme.palette.text.primary
                }}
            >
                <Toolbar sx={{ minHeight: 80, height: 80 }}>
                    <IconButton
                        color="inherit"
                        aria-label="abrir menú"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: 'none' }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                        <Typography variant="h6" noWrap component="div" color="inherit" sx={{ fontWeight: 'bold', fontFamily: '"Outfit", "Inter", sans-serif' }}>
                            {window.location.pathname.includes('/dashboard') && 'Panel Principal'}
                            {window.location.pathname.includes('/invoices') && 'Recibos'}
                            {window.location.pathname.includes('/payments') && 'Recibos'}
                            {window.location.pathname.includes('/traffic') && 'Monitoreo de Tráfico'}
                            {window.location.pathname.includes('/promotions') && 'Promociones y Regalos'}
                            {window.location.pathname.includes('/support') && 'Centro de Soporte'}
                            {window.location.pathname.includes('/profile') && 'Mi Perfil'}
                        </Typography>
                    </Box>

                    {/* Saludo personalizado visible solo en móviles */}
                    <Typography
                        variant="h6"
                        noWrap
                        component="div"
                        sx={{
                            fontWeight: 'bold',
                            fontFamily: '"Outfit", "Inter", sans-serif',
                            display: { xs: 'block', md: 'none' },
                            color: 'inherit'
                        }}
                    >
                        Hola, {firstName}
                    </Typography>

                    <Box sx={{ flexGrow: 1 }} />

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {/* Selector de servicio si el cliente tiene más de uno */}
                        {services && services.length > 1 && (
                            <Box sx={{ mr: 2, display: { xs: 'none', md: 'block' } }}>
                                <Button
                                    id="service-select-button"
                                    aria-controls={openServiceMenu ? 'service-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={openServiceMenu ? 'true' : undefined}
                                    variant="outlined"
                                    onClick={handleServiceMenuClick}
                                    endIcon={<ExpandMoreIcon sx={{ color: theme.palette.text.primary }} />}
                                    sx={{
                                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(30, 41, 59, 0.12)',
                                        bgcolor: theme.palette.background.paper,
                                        color: theme.palette.text.primary,
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(21, 101, 192, 0.04)',
                                        },
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        px: 2,
                                        py: 0.75,
                                        fontSize: '0.85rem',
                                        maxWidth: 240,
                                        textTransform: 'none',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <WifiIcon sx={{ mr: 1, fontSize: 16, color: '#1565c0' }} />
                                    {clientServiceLabel}
                                </Button>
                                <Menu
                                    id="service-menu"
                                    anchorEl={serviceMenuAnchor}
                                    open={openServiceMenu}
                                    onClose={handleServiceMenuClose}
                                    MenuListProps={{
                                        'aria-labelledby': 'service-select-button',
                                    }}
                                    PaperProps={{
                                        elevation: 3,
                                        sx: {
                                            mt: 1,
                                            minWidth: 320,
                                            borderRadius: 2,
                                            border: '1px solid rgba(0,0,0,0.08)',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                                        }
                                    }}
                                >
                                    {services.map((srv) => {
                                        const isCurrent = srv.id === client?.id;
                                        return (
                                            <MenuItem
                                                key={srv.id}
                                                selected={isCurrent}
                                                onClick={() => handleServiceSelect(srv.id)}
                                                sx={{
                                                    py: 1.5,
                                                    px: 2,
                                                    borderLeft: isCurrent ? '4px solid #1565c0' : '4px solid transparent',
                                                    bgcolor: isCurrent ? 'rgba(21, 101, 192, 0.04)' : 'transparent',
                                                    '&.Mui-selected': {
                                                        bgcolor: 'rgba(21, 101, 192, 0.08)',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(21, 101, 192, 0.12)',
                                                        }
                                                    }
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 0.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ flexGrow: 1, color: 'text.primary', noWrap: true, textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: 200 }}>
                                                            {srv.alias || 'Línea de Internet'}
                                                        </Typography>
                                                        
                                                        <Tooltip title="Editar nombre (alias)">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation(); // Evitar cambiar de servicio al hacer clic en el lápiz!
                                                                    handleServiceMenuClose();
                                                                    handleOpenEditAliasDialog(srv);
                                                                }}
                                                                sx={{ 
                                                                    p: 0.5, 
                                                                    color: 'text.secondary', 
                                                                    '&:hover': { color: 'primary.main', bgcolor: 'rgba(21, 101, 192, 0.08)' } 
                                                                }}
                                                            >
                                                                <EditIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Box
                                                            sx={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: '50%',
                                                                bgcolor: srv.status === 'active' ? '#4caf50' : '#f44336',
                                                                flexShrink: 0
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', noWrap: true, textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: 240 }}>
                                                        Dirección: {srv.address}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                        Plan: {srv.plan} • IP: {srv.ipAddress}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                </Menu>
                            </Box>
                        )}

                        {/* Centro de notificaciones con soporte de socket - visible en móvil y desktop */}
                        <NotificationCenter />

                        {/* Selector de modo claro/oscuro */}
                        <Tooltip title={theme.palette.mode === 'dark' ? 'Cambiar a Modo Claro' : 'Cambiar a Modo Oscuro'}>
                            <IconButton
                                onClick={toggleTheme}
                                color="inherit"
                                sx={{ 
                                    ml: 1,
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                                    }
                                }}
                            >
                                {theme.palette.mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                            </IconButton>
                        </Tooltip>

                        {/* Botón de cerrar sesión exclusivo para móviles */}
                        <IconButton
                            color="inherit"
                            onClick={() => logoutClient()}
                            sx={{ display: { xs: 'flex', md: 'none' }, ml: 1 }}
                            title="Cerrar Sesión"
                        >
                            <LogoutIcon />
                        </IconButton>

                        <Box
                            sx={{
                                display: { xs: 'none', md: 'flex' },
                                alignItems: 'center',
                                ml: 2,
                                cursor: 'pointer',
                                borderRadius: 2,
                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                                p: 0.5,
                                pr: 2
                            }}
                            onClick={handleProfileMenuOpen}
                        >
                            <Avatar
                                sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: '#1565c0',
                                    color: '#ffffff',
                                    boxShadow: '0 2px 8px rgba(21, 101, 192, 0.15)',
                                    fontWeight: 'bold'
                                }}
                            >
                                {client?.fullName?.charAt(0) || 'C'}
                            </Avatar>
                            <Box sx={{ ml: 1.5, display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="subtitle2" noWrap sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
                                    {client?.fullName || 'Cliente'}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                                        {client?.plan || 'Plan Básico'}
                                    </Typography>
                                    {!isActive && (
                                        <Typography variant="caption" sx={{ ml: 1, display: 'flex', alignItems: 'center', color: 'error.main', fontWeight: 'medium' }}>
                                            <WarningIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                                            Suspendido
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'none', ml: 1 }}>
                        <IconButton
                            aria-label="mostrar más"
                            aria-controls="mobile-menu"
                            aria-haspopup="true"
                            onClick={handleMobileMenuOpen}
                            color="inherit"
                        >
                            <MoreIcon />
                        </IconButton>
                    </Box>
                </Toolbar>

                {/* Alerta de servicio inactivo */}
                {showInactiveAlert && !isActive && (
                    <Alert
                        severity="error"
                        variant="filled"
                        icon={<WarningIcon />}
                        action={
                            <>
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() => navigate('/client/payments', { state: { autoOpenPayment: true } })}
                                    sx={{ mr: 1 }}
                                >
                                    REALIZAR PAGO
                                </Button>
                                <IconButton
                                    color="inherit"
                                    size="small"
                                    onClick={handleDismissAlert}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </>
                        }
                    >
                        Su servicio se encuentra suspendido por falta de pago. Realice un pago para reactivarlo.
                    </Alert>
                )}
            </AppBar>

            {/* Menú móvil */}
            <Menu
                anchorEl={mobileMoreAnchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                id="mobile-menu"
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={isMobileMenuOpen}
                onClose={handleMobileMenuClose}
            >
                {mobileMenuItems}
            </Menu>

            {/* Menú de perfil */}
            <Menu
                anchorEl={profileMenuAnchor}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                id="profile-menu"
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={isProfileMenuOpen}
                onClose={handleProfileMenuClose}
            >
                {profileMenuItems}
            </Menu>

            <Box
                component="nav"
                sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Mejor rendimiento en móviles
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            background: theme.palette.mode === 'dark' ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)' : 'linear-gradient(180deg, #1565c0 0%, #1e88e5 100%)',
                            color: '#ffffff',
                            borderRight: 'none'
                        },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            background: theme.palette.mode === 'dark' ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)' : 'linear-gradient(180deg, #1565c0 0%, #1e88e5 100%)',
                            color: '#ffffff',
                            borderRight: 'none'
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 2, sm: 4 }, // Ajustado a 4 en pantallas grandes para dar un look amplio y premium
                    pb: { xs: 10, sm: 4 },
                    width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
                    minHeight: '100vh',
                    bgcolor: theme.palette.mode === 'dark' ? '#0f172a' : '#f5f7fb',
                    pt: { xs: showInactiveAlert && !isActive ? 18 : 10, sm: showInactiveAlert && !isActive ? 18 : 10 }, // Altura compensada para barra de 80px
                    overflowX: 'hidden',
                    maxWidth: '100%'
                }}
            >
                <Outlet />
            </Box>

            {/* Diálogo para Editar Nombre de Servicio (Alias) */}
            <Dialog 
                open={editAliasOpen} 
                onClose={handleCloseEditAliasDialog}
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        p: 1.5,
                        minWidth: { xs: 280, sm: 380 },
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', fontFamily: '"Outfit", "Inter", sans-serif', fontSize: '1.25rem', pb: 1 }}>
                    Personalizar Servicio
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.85rem' }}>
                        Asigna un nombre fácil de recordar a tu servicio de internet contratado en la dirección:
                        <strong style={{ display: 'block', marginTop: '6px', color: '#1565c0', fontSize: '0.9rem' }}>{selectedServiceForAlias?.address}</strong>
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Nombre del Servicio (ej. Casa, Oficina, Local)"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newAliasValue}
                        onChange={(e) => setNewAliasValue(e.target.value)}
                        placeholder="Ej. Mi Casa"
                        inputProps={{ maxLength: 30 }}
                        sx={{
                            mt: 1,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button 
                        onClick={handleCloseEditAliasDialog} 
                        color="inherit"
                        sx={{ textTransform: 'none', fontWeight: 'bold', fontSize: '0.85rem' }}
                    >
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleSaveAlias} 
                        variant="contained"
                        disabled={savingAlias}
                        sx={{ 
                            textTransform: 'none', 
                            fontWeight: 'bold', 
                            borderRadius: 2.5,
                            fontSize: '0.85rem',
                            px: 3,
                            bgcolor: '#1565c0',
                            '&:hover': { bgcolor: '#0d47a1' },
                            boxShadow: '0 4px 12px rgba(21, 101, 192, 0.25)'
                        }}
                    >
                        {savingAlias ? 'Guardando...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Menú emergente de tres puntos para el móvil */}
            <Menu
                id="bottom-more-menu"
                anchorEl={moreMenuAnchor}
                open={openMoreMenu}
                onClose={handleMoreMenuClose}
                PaperProps={{
                    elevation: 5,
                    sx: {
                        borderRadius: 3,
                        minWidth: 160,
                        border: '1px solid rgba(0,0,0,0.08)',
                        boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
                        mt: -1.5,
                        '& .MuiMenuItem-root': {
                            fontSize: '0.9rem',
                            py: 1.2
                        }
                    }
                }}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
            >
                <MenuItem onClick={() => { handleMoreMenuClose(); navigate('/client/profile'); }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                        <AccountIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText primary="Mi Perfil" primaryTypographyProps={{ fontWeight: 600 }} />
                </MenuItem>
                <MenuItem onClick={() => { handleMoreMenuClose(); navigate('/client/traffic'); }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                        <SpeedIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText primary="Tráfico" primaryTypographyProps={{ fontWeight: 600 }} />
                </MenuItem>
            </Menu>

            {/* Navegación inferior fija para celulares */}
            <Paper 
                elevation={10} 
                sx={{ 
                    position: 'fixed', 
                    bottom: 0, 
                    left: 0, 
                    right: 0, 
                    zIndex: 1000,
                    display: { xs: 'block', sm: 'none' },
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderRadius: '16px 16px 0 0',
                    overflow: 'visible',
                    boxShadow: '0 -4px 20px rgba(0,0,0,0.06)',
                    background: theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)'
                }}
            >
                <BottomNavigation
                    value={
                        window.location.pathname.includes('/client/dashboard') ? 0 :
                        window.location.pathname.includes('/client/payments') ? 1 :
                        window.location.pathname.includes('/client/promotions') ? 2 :
                        window.location.pathname.includes('/client/support') ? 3 :
                        (window.location.pathname.includes('/client/profile') || window.location.pathname.includes('/client/traffic')) ? 4 : false
                    }
                    onChange={(event, newValue) => {
                        if (newValue === 4) return;
                        const paths = [
                            '/client/dashboard',
                            '/client/payments',
                            '/client/promotions',
                            '/client/support'
                        ];
                        navigate(paths[newValue]);
                    }}
                    showLabels
                    sx={{
                        height: 64,
                        bgcolor: 'transparent',
                        '& .MuiBottomNavigationAction-root': {
                            minWidth: 'auto',
                            padding: '6px 0',
                            color: 'text.secondary',
                            overflow: 'visible',
                            '&.Mui-selected': {
                                color: 'primary.main',
                                '& .MuiSvgIcon-root': {
                                    transform: 'scale(1.1)',
                                    transition: 'transform 0.2s ease-in-out'
                                }
                            }
                        }
                    }}
                >
                    <BottomNavigationAction label="Inicio" icon={<HomeIcon />} />
                    <BottomNavigationAction label="Recibos" icon={<PaymentIcon />} />
                    <BottomNavigationAction 
                        label="Promociones" 
                        icon={
                            <Box sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #f43f5e 0%, #d946ef 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#ffffff',
                                boxShadow: '0 4px 14px rgba(217, 70, 239, 0.4)',
                                mb: 0.5,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '@keyframes bounceWiggle': {
                                    '0%, 100%': { transform: 'translateY(-8px) scale(1)', boxShadow: '0 4px 14px rgba(217, 70, 239, 0.4)' },
                                    '50%': { transform: 'translateY(-14px) scale(1.1) rotate(6deg)', boxShadow: '0 8px 20px rgba(217, 70, 239, 0.6)' },
                                    '70%': { transform: 'translateY(-14px) scale(1.1) rotate(-6deg)', boxShadow: '0 8px 20px rgba(217, 70, 239, 0.6)' },
                                },
                                animation: 'bounceWiggle 3s infinite ease-in-out',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #e11d48 0%, #c084fc 100%)',
                                    transform: 'translateY(-16px) scale(1.15)',
                                    boxShadow: '0 10px 24px rgba(217, 70, 239, 0.7)'
                                }
                            }}>
                                <GiftIcon sx={{ fontSize: 24, color: '#ffffff' }} />
                            </Box>
                        } 
                        sx={{
                            '&.MuiBottomNavigationAction-root': {
                                overflow: 'visible',
                                '& .MuiBottomNavigationAction-label': {
                                    transform: 'translateY(-2px)',
                                    fontWeight: 'medium',
                                    fontSize: '0.65rem',
                                    transition: 'all 0.2s',
                                    color: 'text.secondary'
                                },
                                '&.Mui-selected': {
                                    '& .MuiBottomNavigationAction-label': {
                                        fontWeight: 'bold',
                                        fontSize: '0.7rem',
                                        color: '#d946ef'
                                    }
                                }
                            }
                        }}
                    />
                    <BottomNavigationAction label="Soporte" icon={<SupportIcon />} />
                    <BottomNavigationAction 
                        label="Más" 
                        icon={<MoreIcon />} 
                        onClick={(event) => setMoreMenuAnchor(event.currentTarget)}
                        sx={{
                            '&.Mui-selected': {
                                color: 'primary.main'
                            }
                        }}
                    />
                </BottomNavigation>
            </Paper>

            {/* Botón flotante de ayuda / tutorial */}
            <Tooltip title="Ver Guía de Uso del Portal" placement="left">
                <Fab
                    onClick={() => setTutorialOpen(true)}
                    sx={{
                        position: 'fixed',
                        bottom: { xs: 80, sm: 24 }, // Para que no tape la barra de navegación inferior móvil
                        right: 24,
                        background: 'linear-gradient(135deg, #38bdf8 0%, #c084fc 100%)',
                        color: '#ffffff',
                        zIndex: 1000,
                        boxShadow: '0 8px 32px rgba(56, 189, 248, 0.4)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #a855f7 100%)',
                            transform: 'scale(1.1) rotate(10deg)',
                            boxShadow: '0 12px 40px rgba(168, 85, 247, 0.6)'
                        },
                        '@keyframes pulseHelp': {
                            '0%': { boxShadow: '0 0 0 0 rgba(56, 189, 248, 0.4)' },
                            '70%': { boxShadow: '0 0 0 15px rgba(56, 189, 248, 0)' },
                            '100%': { boxShadow: '0 0 0 0 rgba(56, 189, 248, 0)' }
                        },
                        animation: 'pulseHelp 2s infinite'
                    }}
                    aria-label="help"
                >
                    <HelpIcon />
                </Fab>
            </Tooltip>

            {/* Diálogo del Tutorial */}
            <HelpTutorialDialog open={tutorialOpen} onClose={() => setTutorialOpen(false)} />
            <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} companyName={companySettings.company_name} />
            <ComplaintsBookModal open={complaintsOpen} onClose={() => setComplaintsOpen(false)} companyName={companySettings.company_name} />
        </Box>
    );
};

export default ClientLayout;